import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { Link } from "react-router-dom";
import { CSVLink } from "react-csv";
import currency from "currency.js";
import { Helmet } from "react-helmet";
import { Breadcrumb, BreadcrumbItem } from "reactstrap";
import moment from "moment";
import { toast } from "react-toastify";
import { Dropdown, Form, Row } from "react-bootstrap";
import { SummaryChartIconUp, SummaryCloseIcon } from "../icons";
import { ExcelIcon, ExportIcon } from "../icons";
import { useDownloadExcel } from "../useDownloadExcel";
import { URLS } from "../urls";
import ModalLoader from "../ModalLoader";
import TableComponent from "../TableComponent";
import { allMonths, fetchActionsUtil, getQueryParams } from "../../utils";
import NoTableItem from "../NoTableItem";

export function PayHistoryComponent({
  TopBar,
  SideBar,
  branch_id,
  SwitchCompanyComponent,
}) {
  const query = getQueryParams();

  const date = new Date();

  const [payrollDate, setPayrollDate] = useState({
    payrollMonth: query?.month ? parseInt(query?.month) : date.getMonth() + 1,
    payrollYear: date.getFullYear(),
  });

  const [payroll, setPayroll] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [totalGross, setTotalGross] = useState(0);
  const [totalNet, setTotalNet] = useState(0);
  const [totalDeduct, setTotalDeduct] = useState(0);

  const [totalAllowance, setTotalAllowance] = useState(0);
  const [totalBonus, setTotalBonus] = useState(0);
  const [totalBenefit, setTotalBenefit] = useState(0);
  const [retaireIt, setRetaireIt] = useState(false);

  const [btnSummryActive, setBtnSummaryActive] = useState(false);
  const [selectedData, setSelectedData] = useState([]);
  const [employeeType, setEmployeeType] = useState("All");
  const [companySettings, setcompanySettings] = useState({});

  const [customHeaders, setCustomHeaders] = useState([]);
  const [payrollSetting, setPayrollSetting] = useState([]);
  const [excelName, setExcelName] = useState("Payroll History");

  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);

  const [salaryType, setSalaryType] = useState("");

  const [excelData, setExcelData] = useState([]);

  const CSVLinkRef = useRef(null);

  // fetch excel hook
  const [isfetchingExcel, fetchExcelData] = useDownloadExcel(
    excelData,
    CSVLinkRef
  );

  // Fetch job categories on component mount
  const fetchAll = useCallback(async () => {
    try {
      const categoryData = fetchActionsUtil(
        `${URLS.backendJobCategory}/get/${branch_id}`,
        "GET"
      );

      const salarySettingsData = fetchActionsUtil(
        `${URLS.backendsalarySetting}/get/${branch_id}`
      );

      const payrollSettingData = fetchActionsUtil(
        `${URLS.backendPayroll}/get-settings/${branch_id}`
      );

      const [payrollSettingResult, salarySettingsResult, categoryResult] =
        await Promise.all([
          payrollSettingData,
          salarySettingsData,
          categoryData,
        ]);

      const salarySettings = salarySettingsResult?.jobGrades[0]?.custom?.map(
        (el) => el.name
      );

      setCustomHeaders(salarySettings);
      setPayrollSetting(payrollSettingResult?.settings);
      setCategories(categoryResult?.jobCategories || []);
    } catch (error) {
      console.error("Error fetching job categories:", error);
    }
  }, [branch_id]);

  useEffect(() => {
    let isMounted = true;
    // Fetch job categories when the component mounts
    if (isMounted) {
      fetchAll();
    }
    // Cleanup function to avoid memory leaks
    return () => {
      setCategories([]);
      isMounted = false;
    };
  }, [fetchAll]);

  const params = useMemo(() => {
    return new URLSearchParams({
      payrollMonth: payrollDate.payrollMonth,
      payrollYear: payrollDate.payrollYear,
      employeeType,
      category,
      fullAndFinally: salaryType,
    });
  }, [payrollDate, employeeType, category, salaryType]);

  const getPayroll = useCallback(async () => {
    setIsLoading(true);

    try {
      // Parallel requests
      const [payrollData, settingsData] = await Promise.all([
        fetchActionsUtil(
          `${URLS.backendPayroll}/get-payroll/${branch_id}?${params}`
        ),
        fetchActionsUtil(`${URLS.backendCompanySettings}/get-settings`, "POST"),
      ]);

      // Process settings
      setcompanySettings({
        ...settingsData.settings,
        extras: settingsData.settings.extras === "true",
      });

      // Handle empty payroll
      const payroll = payrollData.payroll || [];
      if (!payroll.length) {
        setPayroll([]);
        return;
      }

      // Initialize totals
      const totals = {
        gross: 0,
        net: 0,
        deduct: 0,
        allowance: 0,
        bonus: 0,
        benefit: 0,
      };
      let hasRetirement = false;

      // Efficient processing
      for (let i = 0; i < payroll.length; i++) {
        const emp = payroll[i];

        // Accumulate
        totals.gross += emp.ogGrossPay || 0;
        totals.net += emp.netPay || 0;
        totals.deduct += emp.totalDeduction || 0;
        totals.allowance += emp.totalAllowances || 0;
        totals.bonus += emp.totalBonus || 0;
        totals.benefit += emp.totalBenefits || 0;

        // Check retirement
        if (!hasRetirement && emp.retairefee > 0) {
          hasRetirement = true;
          // No break - need full total even if retirement found early
        }
      }

      // Batch state updates
      setPayroll(payroll);
      setTotalGross(totals.gross);
      setTotalNet(totals.net);
      setTotalDeduct(totals.deduct);
      setTotalAllowance(totals.allowance);
      setTotalBonus(totals.bonus);
      setTotalBenefit(totals.benefit);
      setRetaireIt(hasRetirement);
    } catch (err) {
      console.error("Payroll processing failed:", err);
    } finally {
      setIsLoading(false);
    }
  }, [params, branch_id]);

  useEffect(() => {
    let isMounted = true; // Track component mount status

    if (isMounted) {
      getPayroll();
    }
    return () => {
      isMounted = false; // Cleanup on unmount
    };
  }, [getPayroll]);

  const onDownloadExcelData = async () => {
    if (!payroll?.length > 0) return toast.error("Nothing to download!!!");

    setExcelName("Payroll History");

    let exData = payroll.map((employee, index) => [
      index + 1,
      `${employee.user?.personalInfo?.name.first || ""} ${
        employee.user?.personalInfo?.name?.middle || ""
      } ${employee.user?.personalInfo?.name.last || ""}`,
      employee.employeeId,
      employee.useExcel === "Use Timesheet" ? employee?.sfa_id : null,
      employee.gradeName,

      employee?.userExtras?.DaysCommitted === ""
        ? 0
        : employee?.userExtras?.DaysCommitted,
      companySettings?.extras
        ? employee?.userExtras?.DayOn === ""
          ? 0
          : employee?.userExtras?.DayOn
        : "",

      companySettings?.extras
        ? employee?.userExtras?.TransitDays === ""
          ? 0
          : employee?.userExtras?.TransitDays
        : "",

      companySettings?.extras
        ? employee?.userExtras?.OverStayDays === ""
          ? 0
          : employee?.userExtras?.OverStayDays
        : "",

      companySettings?.extras
        ? employee?.userExtras?.ArrearsDays === ""
          ? 0
          : employee?.userExtras?.ArrearsDays
        : "",

      employee.usedlocation,
      employee.usedcontract,
      employee.useExcel === "Use Timesheet" ? employee?.totalDaysWorked : null,
      employee.useExcel === "Use Timesheet"
        ? parseFloat(employee?.numProration).toFixed(2)
        : null,
      currency(employee.grossPay, {
        symbol: "",
      }).format(),
      currency(employee.basic, {
        symbol: "",
      }).format(),
      currency(employee.housing, {
        symbol: "",
      }).format(),
      currency(employee.transport, {
        symbol: "",
      }).format(),
      currency(employee.medical, {
        symbol: "",
      }).format(),
      currency(employee.utility, {
        symbol: "",
      }).format(),
      currency(employee.entertainment, {
        symbol: "",
      }).format(),
      currency(employee.dressing, {
        symbol: "",
      }).format(),
      // employee.bonuses,
      currency(employee.ogGrossPay, {
        symbol: "",
      }).format(),

      ...employee.deductions
        .filter((el) => !el.groupName)
        .map((deduction, index) => deduction.total),

      ...employee.DeductionGroupName.map((e, i) =>
        currency(
          employee.deductions
            ?.filter((el) => el.groupName === e)
            .map((el) => el.total)
            .reduce((a, b) => a + b, 0),
          {
            symbol: "",
          }
        ).format()
      ),

      ...employee.bonus
        .filter((el) => !el.groupName)
        .map((bonu, index) => bonu.total),

      ...employee.BonusGroupName.map((e, i) =>
        currency(
          employee.bonus
            ?.filter((el) => el.groupName === e)
            .map((el) => el.total)
            .reduce((a, b) => a + b, 0),
          {
            symbol: "",
          }
        ).format()
      ),

      ...employee.allowances
        .filter((el) => !el.groupName)
        .map((allowance, index) => allowance.total),

      ...employee.AllowanceGroupName.map((e, i) =>
        currency(
          employee.allowances
            ?.filter((el) => el.groupName === e)
            .map((el) => el.total)
            .reduce((a, b) => a + b, 0),
          {
            symbol: "",
          }
        ).format()
      ),

      ...employee.benefits
        .filter((el) => !el.groupName)
        .map((benefit, index) => benefit.total),

      ...employee.BenefitGroupName.map((e, i) =>
        currency(
          employee.benefits
            ?.filter((el) => el.groupName === e)
            .map((el) => el.total)
            .reduce((a, b) => a + b, 0),
          {
            symbol: "",
          }
        ).format()
      ),

      currency(employee.attendanceTotal, {
        symbol: "",
      }).format(),
      currency(employee.totalDeduction, {
        symbol: "",
      }).format(),
      currency(employee.totalBonus, {
        symbol: "",
      }).format(),

      currency(employee.totalAllowances, {
        symbol: "",
      }).format(),
      currency(employee.totalBenefits, {
        symbol: "",
      }).format(),
      currency(employee.loanDeduct, {
        symbol: "",
      }).format(),
      employee.remark,
      currency(employee.netPay, {
        symbol: "",
      }).format(),
    ]);

    const normalHead = [
      "No",
      "Name",
      "Employees ID",
      payroll[0].useExcel === "Use Timesheet" ? "SFA ID" : null,
      "Job Grade",
      "Days Committed",
      companySettings?.extras ? "Days On" : "",
      companySettings?.extras ? "Transit Days" : "",
      companySettings?.extras ? "Overstay Days" : "",
      companySettings?.extras ? "Arrears Days" : "",
      "Location",
      "LSP",
      payroll[0].useExcel === "Use Timesheet" ? "Days Worked" : null,
      payroll[0].useExcel === "Use Timesheet" ? "Proration" : null,
      "Gross Salary",
      "Basic",
      "Housing",
      "Transport",
      "Medical",
      "Utility",
      "Entertainment",
      "Dressing",
      "Earned Gross",

      ...payroll[0]?.deductions
        .filter((el) => !el.groupName)
        .map((setting, index) => setting.name),

      ...payroll[0]?.DeductionGroupName.map((setting, index) => setting),

      ...payroll[0]?.bonus
        .filter((el) => !el.groupName)
        .map((setting, index) => setting.name),

      ...payroll[0]?.BonusGroupName.map((setting, index) => setting),

      ...payroll[0]?.allowances
        .filter((el) => !el.groupName)
        .map((setting, index) => setting.name),

      ...payroll[0]?.AllowanceGroupName.map((setting, index) => setting),

      ...payroll[0]?.benefits
        .filter((el) => !el.groupName)
        .map((setting, index) => setting.name),

      ...payroll[0]?.BenefitGroupName.map((setting, index) => setting),

      "Attendance Deduction",
      "Total Deductions",
      "Total Bonus",
      "Total Allowance",
      "Total Benefit",
      "Loan",
      "Remark",
      "Net Pay",
    ];

    const date = `Date Prepared: ${moment(new Date()).format(
      "MMMM DD yyyy k:mm:ss z"
    )}`;

    exData = [
      // [company],
      ["Payment History Report"],
      [date],
      [""],
      normalHead,
      ...exData,
      [""],
      ["Payhistory Summary"],
      [
        "Total Earned Gross ",
        currency(totalGross, {
          symbol: "",
        }).format(),
      ],
      [
        "Total Allowance",
        currency(totalAllowance, {
          symbol: "",
        }).format(),
      ],
      [
        "Total Bonus",
        currency(totalBonus, {
          symbol: "",
        }).format(),
      ],

      [
        "Total Benefit",
        currency(totalBenefit, {
          symbol: "",
        }).format(),
      ],
      [
        "Total Deduction",
        currency(totalDeduct, {
          symbol: "",
        }).format(),
      ],
      [
        "Total Net Amount",
        currency(totalNet, {
          symbol: "",
        }).format(),
      ],
    ];

    // console.log(exData);
    setExcelData(exData);
  };

  const onDownloadExcelPayeeTax = async () => {
    if (!payroll?.length > 0) return toast.error("Nothing to download!!!");
    setExcelName("PayE Tax Computation");

    let taxNames = [];

    const computation = payrollSetting?.taxRateAsPerMonthlyComputation.sort(
      (a, b) => a.ratePercentage - b.ratePercentage
    );

    let exData = payroll.map((employee, index) => {
      let totalExemption = [];
      if (payrollSetting?.exemptionDeductions?.length > 0) {
        totalExemption = employee?.deductions?.filter(({ _id }) =>
          payrollSetting?.exemptionDeductions?.find((ele) => _id === ele)
        );

        totalExemption = totalExemption?.map((el) => ({
          amount: el?.total,
          name: el?.groupName,
        }));

        taxNames = totalExemption;
      }

      const computationTax = employee?.taxRateArray?.sort(
        (a, b) => a.percent - b.percent
      );

      // console.log({ taxNames, totalExemption });

      return [
        index + 1,
        `${employee.user?.personalInfo?.name.first || ""} ${
          employee.user?.personalInfo?.name?.middle || ""
        } ${employee.user?.personalInfo?.name.last || ""}`,
        employee.employeeId,
        payroll[0].useExcel === "Use Timesheet" ? employee?.sfa_id : "",
        employee.gradeName,
        currency(employee.grossPay, {
          symbol: "",
        }).format(),
        currency(employee.basic, {
          symbol: "",
        }).format(),
        currency(employee.housing, {
          symbol: "",
        }).format(),
        currency(employee.transport, {
          symbol: "",
        }).format(),
        currency(employee.medical, {
          symbol: "",
        }).format(),
        currency(employee.utility, {
          symbol: "",
        }).format(),
        currency(employee.entertainment, {
          symbol: "",
        }).format(),
        currency(employee.dressing, {
          symbol: "",
        }).format(),
        ...employee?.custom?.map((el) =>
          currency(el?.value, {
            symbol: "",
          }).format()
        ),
        currency(employee.ogGrossPay, {
          symbol: "",
        }).format(),

        ...totalExemption?.map((el) =>
          currency(el?.amount, {
            symbol: "",
          }).format()
        ),
        currency(employee?.totalExemption, {
          symbol: "",
        }).format(),
        currency(employee?.CRAGrossPurpose, {
          symbol: "",
        }).format(),
        currency(employee?.CRAGrossPurposePercentage, {
          symbol: "",
        }).format(),
        currency(employee?.personalAllowance, {
          symbol: "",
        }).format(),
        currency(employee?.totalRelief, {
          symbol: "",
        }).format(),
        currency(employee?.totalExemption, {
          symbol: "",
        }).format(),
        currency(employee?.chargeableIncome, {
          symbol: "",
        }).format(),
        ...computationTax.map((ele, i) =>
          currency(ele?.amount, {
            symbol: "",
          }).format()
        ),
        currency(employee?.taxRateAsPerMonthlyTotal, {
          symbol: "",
        }).format(),
      ];
    });

    const normalHead = [
      "No",
      "Name",
      "Employees ID",
      "SFA ID",
      "Job Grade",
      "Gross Salary",
      "Basic",
      "Housing",
      "Transport",
      "Medical",
      "Utility",
      "Entertainment",
      "Dressing",
      ...customHeaders.map((el) => el),
      "Earned Gross Salary",
      ...taxNames.map((el) => el?.name),
      "Total Exempt",
      "Gross For CRA Purpose",
      `Gross For CRA Purpose (${payrollSetting?.grossForCraPurposePercentage}%)`,
      `Personal Allowance ${payrollSetting?.personalAllowance}/PA`,
      "Total Relief",
      "Total Deductions",
      "Chargeble Income",
    ];

    const date = `Date Prepared: ${moment(new Date()).format(
      "MMMM DD yyyy k:mm:ss z"
    )}`;

    exData = [
      // [company],
      ["Paye Tax Computation"],
      [date],
      [""],
      [
        ...normalHead?.map((ele) => ""),
        ...computation.map((el) => `${el?.ratePercentage}%`),
      ],
      [
        ...normalHead,
        ...computation.map((el) =>
          currency(el?.rateAmount, {
            symbol: "",
          }).format()
        ),
        "TAX PAYABLE",
      ],
      ...exData,
      [""],
    ];

    // console.log(exData);
    setExcelData(exData);
  };

  const onSelected = (userId) => {
    setSelectedData((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const emailPayslip = async () => {
    setIsLoading(true);

    try {
      const selectedSet = new Set(selectedData);

      const emailPayroll = payroll.reduce((acc, e) => {
        if (selectedSet.has(e.userId)) {
          acc.push({
            email: e.user?.personalInfo?.email,
            userId: e.userId,
            _id: e._id,
            payrollId: e._id,
            payrollMonth: payrollDate.payrollMonth,
            payrollYear: payrollDate.payrollYear,
          });
        }
        return acc;
      }, []);

      const token = "dummy token";

      const data = await fetchExcelData(
        `${URLS.backendPayroll}/email-payrolls`,
        "POST",
        token,
        emailPayroll
      );

      toast[data.success ? "success" : "error"](
        data.success ? "Email sent successfully" : "Error!!"
      );
    } catch (err) {
      console.log(err);
      toast.error("Unexpected error");
    } finally {
      setIsLoading(false);
    }
  };

  // Memoized payroll IDs for performance
  const payrollIds = useMemo(() => {
    if (!selectedData.length || !payroll.length) return [];

    // Convert selectedData to a Set for O(1) lookups
    const selectedSet = new Set(selectedData);
    const ids = [];

    // Use for loop for better performance
    for (let i = 0; i < payroll.length; i++) {
      const employee = payroll[i];
      if (selectedSet.has(employee.userId)) {
        ids.push(employee._id);
      }
    }

    return ids;
  }, [selectedData, payroll]);

  const onSelectedAll = (e) => {
    setSelectedData(e.target.checked ? payroll.map((d) => d.userId) : []);
  };

  // Table head function
  const tableHead = () => {
    if (!payroll?.length) return null;

    const p0 = payroll[0];
    const headers = p0?.settingHeaders || {};
    const useTimesheet = p0?.useExcel === "Use Timesheet";

    const shouldShow = (field) =>
      Object.keys(headers).length === 0 || headers[field];

    const renderHeaderCells = (arr, isGroup = false) =>
      arr?.map((item, idx) => (
        <th className="text-capitalize" key={`${item}-${idx}`}>
          {isGroup ? (
            <>
              {item}
              <br />
            </>
          ) : item.name === "company pension" ? (
            "Employer pension contribution"
          ) : item.name === "pension" ? (
            "Employee pension contribution"
          ) : (
            item.name
          )}
        </th>
      ));

    return (
      <thead className="position-sticky top-0">
        <tr>
          <th>
            <input
              type="checkbox"
              onChange={onSelectedAll}
              checked={selectedData?.length === payroll?.length}
            />
          </th>
          <th>No</th>
          <th>Name</th>
          <th>Employees ID</th>
          {useTimesheet && <th>SFA ID</th>}
          <th>Job Grade</th>
          <th>Days Committed</th>
          {companySettings?.extras && (
            <>
              <th>Days On</th>
              <th>Transit Days</th>
              <th>Overstay Days</th>
              <th>Arrears Days</th>
            </>
          )}

          <th>Location</th>
          <th>LSP</th>

          {useTimesheet && (
            <>
              <th>Days Worked</th>
              <th>Proration</th>
            </>
          )}

          <th>Gross Salary</th>

          {shouldShow("basic") && <th>Basic</th>}
          {shouldShow("housing") && <th>Housing</th>}
          {shouldShow("transport") && <th>Transport</th>}
          {shouldShow("medical") && <th>Medical</th>}
          {shouldShow("utility") && <th>Utility</th>}
          {shouldShow("entertainment") && <th>Entertainment</th>}
          {shouldShow("dressing") && <th>Dressing</th>}

          {p0?.customHeaders?.map((el, i) => (
            <th key={`custom-${i}`}>{el}</th>
          ))}

          <th>Earned Gross Salary</th>

          {/* Deductions */}
          {renderHeaderCells(p0?.deductions?.filter((d) => !d.groupName))}
          {renderHeaderCells(p0?.DeductionGroupName || [], true)}

          {/* Bonuses */}
          {renderHeaderCells(p0?.bonus?.filter((b) => !b.groupName))}
          {renderHeaderCells(p0?.BonusGroupName || [], true)}

          {/* Allowances */}
          {renderHeaderCells(p0?.allowances?.filter((a) => !a.groupName))}
          {renderHeaderCells(p0?.AllowanceGroupName || [], true)}

          {/* Benefits */}
          {renderHeaderCells(p0?.benefits?.filter((b) => !b.groupName))}
          {renderHeaderCells(p0?.BenefitGroupName || [], true)}

          <th>Loan</th>
          {retaireIt && <th>IOU Deductions</th>}
          <th>Attendance Deduction</th>
          <th>Total Deductions</th>
          <th>Total Bonus</th>
          <th>Total Allowance</th>
          <th>Total Benefit</th>
          <th>Remark</th>
          <th>Net Pay</th>
          <th>Payslip</th>
        </tr>
      </thead>
    );
  };

  // Table body data function
  const tableBodyData = (employee, index) => {
    const {
      user,
      settingHeaders = {},
      deductions = [],
      bonus = [],
      allowances = [],
      benefits = [],
      custom = [],
      userExtras = {},
    } = employee;

    const name = `${user?.personalInfo?.name?.first ?? ""} ${
      user?.personalInfo?.name?.middle ?? ""
    } ${user?.personalInfo?.name?.last ?? ""} `.trim();

    const isChecked = selectedData.includes(employee.userId);
    const UseTimeSheet = employee.useExcel === "Use Timesheet";
    const hasExtras = companySettings?.extras;

    // Grouped sums (avoids repeated filtering/reducing)
    const groupSum = (arr, group) =>
      currency(
        arr
          .filter((el) => el.groupName === group)
          .reduce((sum, el) => sum + (el.total || 0), 0),
        { symbol: "" }
      ).format();

    const ungroupedCells = (arr) =>
      arr
        .filter((el) => !el.groupName)
        .map((item, i) => (
          <td key={i}>{currency(item.total || 0, { symbol: "" }).format()}</td>
        ));

    const groupCells = (arr, names) =>
      names.map((name, i) => <td key={i}>{groupSum(arr, name)}</td>);

    const currencyCell = (val) => (
      <td>{currency(val || 0, { symbol: "" }).format()}</td>
    );

    const normalCell = (val) => <td>{val}</td>;

    return (
      <>
        <td>
          <input
            type="checkbox"
            onChange={() => onSelected(employee.userId)}
            checked={isChecked}
          />
        </td>
        <td>{index + 1}</td>
        <td>{name.trim()}</td>
        <td>{employee.employeeId}</td>
        {UseTimeSheet && <td>{employee.sfa_id}</td>}
        <td>{employee.gradeName}</td>
        {normalCell(userExtras.DaysCommitted || 0)}
        {hasExtras && (
          <>
            {normalCell(userExtras.DayOn || 0)}
            {normalCell(userExtras.TransitDays || 0)}
            {normalCell(userExtras.OverStayDays || 0)}
            {normalCell(userExtras.ArrearsDays || 0)}
          </>
        )}

        <td>{employee.usedlocation}</td>
        <td>{employee.usedcontract}</td>

        {UseTimeSheet && (
          <>
            <td>{employee.totalDaysWorked}</td>
            <td>{parseFloat(employee.numProration || 0).toFixed(2)}</td>
          </>
        )}

        {currencyCell(employee.grossPay)}

        {[
          "basic",
          "housing",
          "transport",
          "medical",
          "utility",
          "entertainment",
          "dressing",
        ].map(
          (key) =>
            (!Object.keys(settingHeaders).length || settingHeaders[key]) &&
            currencyCell(employee[key])
        )}

        {custom.map((el, i) => (
          <td key={i}>{currency(el?.value || 0, { symbol: "" }).format()}</td>
        ))}

        {currencyCell(employee.ogGrossPay)}

        {ungroupedCells(deductions)}
        {groupCells(deductions, employee.DeductionGroupName || [])}
        {ungroupedCells(bonus)}
        {groupCells(bonus, employee.BonusGroupName || [])}
        {ungroupedCells(allowances)}
        {groupCells(allowances, employee.AllowanceGroupName || [])}
        {ungroupedCells(benefits)}
        {groupCells(benefits, employee.BenefitGroupName || [])}

        {currencyCell(employee.loanDeduct)}

        {retaireIt && currencyCell(employee.remainingRetairment)}

        {currencyCell(employee.attendanceTotal)}
        {currencyCell(employee.allTotalDeduction)}
        {currencyCell(employee.totalBonus)}
        {currencyCell(employee.totalAllowances)}
        {currencyCell(employee.totalBenefits)}

        <td>{employee.remark}</td>

        {currencyCell(employee.netPay)}

        <td style={{ fontSize: "22px" }}>
          <a
            className="mx-2 text-primary"
            target="_blank"
            rel="noopener noreferrer"
            href={`${URLS.backendPayroll}/view-payslip/${employee._id}`}
            title="View slip"
          >
            <i className="la la-eye"></i>
          </a>
        </td>
      </>
    );
  };

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="robots" content="noindex, nofollow"></meta>
        <title>InvexERP</title>
      </Helmet>
      <div className="main-wrapper">
        <TopBar />
        <SideBar />
        {/*<!-- Page Wrapper -->*/}
        <div className="page-wrapper">
          {/*<!-- Page Content -->*/}
          <div className="content container-fluid">
            {/*<!-- Page Title -->*/}
            <div className="row">
              <div className="col-sm-12 col-12 text-capitalize">
                <Breadcrumb>
                  <BreadcrumbItem>
                    <Link to={`${URLS.dashboard}`}>Dashboard</Link>
                  </BreadcrumbItem>
                  <BreadcrumbItem active>Pay History</BreadcrumbItem>
                </Breadcrumb>
              </div>
              <div className="col-sm-5 col-5"></div>
              {/*<div className="col-sm-7 col-7 text-right m-b-30"></div>*/}
            </div>
            {/*<!-- /Page Title -->*/}
            {/*<!-- Content Starts -->*/}
            <div className="mt-3">
              <div className="d-flex justify-content-between my-4">
                <div className="d-flex">
                  <div className="form-inline">
                    <label>Month</label>
                    <select
                      className="form-control mx-2"
                      value={payrollDate.payrollMonth}
                      onChange={(e) =>
                        setPayrollDate({
                          payrollMonth: e.target.value,
                          payrollYear: payrollDate.payrollYear,
                        })
                      }
                    >
                      {[...Array(12)].map((e, index) => (
                        <option key={index}>{index + 1}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-inline ml-3">
                    <label>Year</label>
                    <input
                      type="number"
                      style={{ width: "100px" }}
                      className="form-control mx-2"
                      value={payrollDate.payrollYear}
                      onChange={(e) =>
                        setPayrollDate({
                          payrollMonth: payrollDate.payrollMonth,
                          payrollYear: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>

                <div className="d-flex">
                  <select
                    className="form-control mx-2"
                    aria-label="Default select example"
                    value={employeeType}
                    onChange={(e) => setEmployeeType(e.target.value)}
                  >
                    <option value="All">All Employee</option>
                    <option value="staff">Staff</option>
                    <option value="contract">Contract</option>
                  </select>
                  <div className="mx-3 w-100">
                    <Form.Control
                      as="select"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      name="category"
                    >
                      <option value="">All Categories</option>
                      {categories && categories.length
                        ? categories.map((category) => {
                            return (
                              <option value={category._id}>
                                {category.name}
                              </option>
                            );
                          })
                        : null}
                    </Form.Control>
                  </div>
                  <div className="mx-2 w-100">
                    <select
                      className="form-control mx-2"
                      aria-label="Default select example"
                      value={salaryType}
                      onChange={(e) => setSalaryType(e.target.value)}
                    >
                      <option value="">Normal Payroll</option>
                      <option value={true}>FullAndFinally</option>
                    </select>
                  </div>
                  <div className="mx-4">
                    <CSVLink
                      className="btn print d-none"
                      filename={`${excelName} (${moment(new Date()).format(
                        "DD-MMM-yyyy hh:mm:ss a"
                      )}).csv`}
                      data={excelData}
                      ref={CSVLinkRef}
                    />

                    <Dropdown>
                      <Dropdown.Toggle
                        variant=""
                        className="print"
                        disabled={isfetchingExcel}
                        bsPrefix=""
                      >
                        <span className="mx-2">Export</span>
                        <ExportIcon color="#008000" />
                      </Dropdown.Toggle>
                      <Dropdown.Menu
                        popperConfig={{
                          strategy: "fixed",
                        }}
                        renderOnMount
                        className="text-center"
                      >
                        <Dropdown.Item
                          as="div"
                          onClick={onDownloadExcelData}
                          className="p-cursor"
                        >
                          Excel <ExcelIcon color="#008000" />
                        </Dropdown.Item>

                        <Dropdown.Item
                          as="div"
                          onClick={onDownloadExcelPayeeTax}
                          className="p-cursor"
                        >
                          PayE Tax Computation
                          <ExcelIcon color="#008000" />
                        </Dropdown.Item>

                        {/* <Dropdown.Item as="div">
                          <a
                            href={`${backendUrl}/api/reports/pdf/sales-analysis?${queryString.stringify(
                              newQuery
                            )}`}
                            target="blank"
                          >
                            PDF
                            <PDFIcon color="#ff0000" />
                          </a>
                        </Dropdown.Item> */}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>

                  <button
                    onClick={() => setBtnSummaryActive(!btnSummryActive)}
                    className="btn summaryBtn "
                  >
                    Show Summary
                  </button>
                </div>
              </div>

              {SwitchCompanyComponent ? (
                <div>
                  <SwitchCompanyComponent />
                </div>
              ) : null}

              <div>
                {payroll.length ? (
                  <Row className="table-height">
                    <TableComponent
                      responsive
                      striped
                      sticky
                      tableHeadsFunction={tableHead}
                      mainDataArray={payroll}
                      tableDataRowFunction={tableBodyData}
                      className="product-table text-nowrap"
                    />
                  </Row>
                ) : (
                  <NoTableItem
                    title={
                      payrollDate.payrollMonth
                        ? `No data on ${
                            allMonths[payrollDate.payrollMonth - 1]
                          } ${payrollDate.payrollYear}`
                        : "Please Select the month for payroll"
                    }
                  />
                )}
              </div>

              {selectedData.length > 0 && (
                <div className="d-flex justify-content-end my-4">
                  <a
                    target="blank"
                    href={`${URLS.backendPayroll}/view-payrolls-pdf?print=${payrollIds}`}
                    title="View slips"
                    className="btn btn-primary mx-4"
                  >
                    View Payslip
                  </a>
                  <button onClick={emailPayslip} className="btn btn-primary">
                    Email Payslip
                  </button>
                </div>
              )}

              <div
                className={
                  btnSummryActive
                    ? `summaryNew border-top open`
                    : `summaryNew border-top`
                }
              >
                <div className="row justify-content-between mt-4">
                  <div className="col">
                    <div className="my-3">
                      <div className="d-flex gap-3 align-items-center mb-4">
                        <h4 className="m-0">Payroll Summary</h4>{" "}
                        <hr className="flex-grow-1 m-0" />
                        {btnSummryActive && (
                          <button
                            onClick={() =>
                              setBtnSummaryActive(!btnSummryActive)
                            }
                            className="btn p-0"
                          >
                            <SummaryCloseIcon />
                          </button>
                        )}
                      </div>

                      <div
                        className="gridCont mb-3 three-col-grid"
                        style={{ gridTemplateRows: "unset" }}
                      >
                        {/*  */}
                        <div className="gridChild gridChildBorderLeftBlue">
                          <div className="gridChildLeft">
                            <p className="gridChld1">
                              {currency(totalGross, {
                                symbol: "",
                              }).format()}
                            </p>
                            <p className="gridChld2">Total Earned Gross </p>
                          </div>

                          <div className="gridChildRight">
                            <SummaryChartIconUp />
                            <p>5%</p>
                          </div>
                        </div>

                        {/*  */}
                        <div className="gridChild gridChildBorderLeftDarkerGreen">
                          <div className="gridChildLeft">
                            <p className="gridChld1">
                              {currency(totalAllowance, {
                                symbol: "",
                              }).format()}
                            </p>
                            <p className="gridChld2">Total Allowance</p>
                          </div>

                          <div className="gridChildRight">
                            <SummaryChartIconUp />
                            <p>5%</p>
                          </div>
                        </div>
                        {/*  */}
                        <div className="gridChild gridChildBorderLeftDarkerGreen">
                          <div className="gridChildLeft">
                            <p className="gridChld1">
                              {currency(totalBonus, {
                                symbol: "",
                              }).format()}
                            </p>
                            <p className="gridChld2">Total Bonus</p>
                          </div>

                          <div className="gridChildRight">
                            <SummaryChartIconUp />
                            <p>5%</p>
                          </div>
                        </div>

                        {/*  */}
                      </div>

                      {/* Second Layer */}

                      <div
                        className="gridCont mb-3 three-col-grid"
                        style={{ gridTemplateRows: "unset" }}
                      >
                        {/*  */}

                        <div className="gridChild gridChildBorderLeftDarkerGreen">
                          <div className="gridChildLeft">
                            <p className="gridChld1">
                              {currency(totalBenefit, {
                                symbol: "",
                              }).format()}
                            </p>
                            <p className="gridChld2">Total Benefit</p>
                          </div>

                          <div className="gridChildRight">
                            <SummaryChartIconUp />
                            <p>5%</p>
                          </div>
                        </div>
                        {/*  */}

                        <div className="gridChild gridChildBorderLeftOrange">
                          <div className="gridChildLeft">
                            <p className="gridChld1">
                              {currency(totalDeduct, {
                                symbol: "",
                              }).format()}
                            </p>
                            <p className="gridChld2">Total Deduction</p>
                          </div>

                          <div className="gridChildRight">
                            <SummaryChartIconUp />
                            <p>5%</p>
                          </div>
                        </div>
                        {/*  */}
                        <div className="gridChild gridChildBorderLeftDarkerGreen">
                          <div className="gridChildLeft">
                            <p className="gridChld1">
                              {currency(totalNet, {
                                symbol: "",
                              }).format()}
                            </p>
                            <p className="gridChld2">Total Net Amount</p>
                          </div>

                          <div className="gridChildRight">
                            <SummaryChartIconUp />
                            <p>5%</p>
                          </div>
                        </div>
                        {/*  */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/*<!-- /Content End -->*/}
          </div>
          {/*<!-- /Page Content -->*/}
        </div>
        {/*<!-- /Page Wrapper -->*/}
      </div>
      <ModalLoader show={isLoading} />
      {/*<!-- /Main Wrapper -->*/} {/* <!-- Sidebar Overlay --> */}
      <div className="sidebar-overlay" data-reff=""></div>
    </div>
  );
}
